/**
 * @file getQuestions.ts
 * Exports a method that for returning an array of question objects.
 */
import { filter, path, prop, includes } from 'ramda';
import type { QuestionData, AppState } from '../types';
import getSubmittedTrueValues from './getSubmittedTrueValues';

/**
 * Helper method that returns an array of question object.
 *
 * Currently all questions are organized in a JSON file. In the future this data
 * may come from an external API.
 *
 * @param {AppState} appState
 *   The state object shared across the application.
 *
 * @returns {array}
 *   An array of Question Data objects.
 */
export default (appState: AppState): QuestionData[] => {
  // Get an array of all currently submitted answers that have a truthy value.
  const submitted = getSubmittedTrueValues(appState);

  // Some of the questions in the source data have a 'showIfTrue' property. This
  // means the question should only be included in the list of questions if
  // another answer has a true value. Remove questions that have this showIfTrue
  // property but do not include a true value in the submitted data.
  const removeHiddenQuestions = (question: object) => {
    const showIfTrue = prop('showIfTrue')(question);
    return !(showIfTrue && !includes(showIfTrue, submitted));
  };

  // Filter the questions list to remove hidden questions.
  return filter(
    removeHiddenQuestions,
    path(['appData', 'questions'], appState),
  );
};
